import type { NodeViewProps } from '@tiptap/react';

import { NodeViewWrapper } from '@tiptap/react';
import { ImagePlusIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import SelectFsItemsDialog from '@/components/features/files/select-fs-items/SelectFsItemsDialog';
import { DropzoneDragArea } from '@/components/ui/fs-dropzone';
import { RemoveFloatButton } from '@/components/ui/tiptap/tiptap-ui-primitives/remove-button';
import { useFileUpload } from '@/hooks/file-upload';

import { type SelectMediaNodeOptions } from '.';

export function SelectMediaNodeComponent(selectMediaNodeComponentProps: NodeViewProps) {
  const { extension, editor, node, getPos } = selectMediaNodeComponentProps;
  const { maxSize, accept, maxFiles } = node.attrs;

  const isEditable: boolean = editor.isEditable;

  const uploadOptions: SelectMediaNodeOptions = {
    maxSize,
    accept,
    maxFiles,
    selectedCallbackFn: extension.options.selectedCallbackFn,
    uploadFileFn: extension.options.uploadFileFn,
    onFileUploaded: extension.options.onFileUploaded,
    onFileUploadError: extension.options.onFileUploadError,
  };

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isError, setError] = useState<boolean>(false);

  const [fileUploadState, fileUploadActions] = useFileUpload({
    maxSize: uploadOptions.maxSize,
    maxFiles: maxFiles ?? 1,
    postponeUpload: false,
    uploadFileFn: async (file, options) => {
      await uploadOptions.uploadFileFn?.(file, options, onRequestResolve);
    },
    onFileUploaded: (file) => {
      uploadOptions.onFileUploaded?.(file);
      setOpenDialog(false); // close dialog if open
    },
    onFileUploadError: (file, error, issue, options) => {
      uploadOptions.onFileUploadError?.(file, error, issue, options);
      setError(true);
    },
  });

  const dropzone = fileUploadActions.createDropzoneProps({ className: 'w-full' });

  const handleRemove = () => {
    const pos: number | undefined = getPos();
    if (pos === undefined) return editor.chain();

    return editor
      .chain()
      .focus()
      .deleteRange({ from: pos, to: pos + 1 });
  };

  const onRequestResolve = async (fileType: string, attrs: Record<string, unknown> | undefined) => {
    const pos: number | undefined = getPos();
    if (pos === undefined) return;
    let nodeType: string | null = null;
    if (fileType.includes('image')) {
      nodeType = 'image-node';
    } else if (fileType.includes('video')) {
      nodeType = 'video-node';
    } else if (fileType.includes('audio')) {
      nodeType = 'audio-node';
    }
    if (!nodeType) return;
    handleRemove()
      .insertContentAt(pos, [
        {
          type: nodeType,
          attrs: attrs,
        },
      ])
      .run();
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  if (!isEditable) {
    return null;
  }

  return (
    <NodeViewWrapper className="relative my-3" tabIndex={0}>
      <RemoveFloatButton editor={editor} pos={getPos()} />
      <SelectFsItemsDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        options={{
          multiple: false,
          maxFiles: 1,
          allow: {
            file: true,
            directory: false,
          },
          accept: uploadOptions.accept,
          maxSize: uploadOptions.maxSize,
          uploadFileFn: async (file, options) => {
            await uploadOptions.uploadFileFn?.(file, options, onRequestResolve);
          },
          onFileUploadError: (file: File, error, issue, options) => {
            if (issue.code === 'FILE_TOO_LARGE') {
              toast.error(`Plik ${file.name} jest zbyt duży i nie został dodany do listy przesyłania.`);
            } else if (issue.code === 'UPLOAD_FAILED') {
              toast.error(`Plik ${file.name} nie został przesłany.`);
            }
            uploadOptions.onFileUploadError?.(file, error, issue, options);
            setError(true);
          },
          selectedCallbackFn: async (selected) => {
            await uploadOptions.selectedCallbackFn?.(selected, onRequestResolve);
          },
          onFileUploaded: (file) => {
            uploadOptions.onFileUploaded?.(file);
          },
        }}
      />
      <DropzoneDragArea {...dropzone.props} isDragging={fileUploadState.isDragging} onClick={handleOpenDialog}>
        <div className="flex flex-col items-center justify-center gap-4">
          <ImagePlusIcon className="text-text-tertiary size-6" />
          <p className={'text-text-secondary text-sm'}>
            <span className={'underline'}>Kliknij aby dodać</span>, albo przeciągnij i upuść plik
          </p>
        </div>
        {isError && <p className="text-text-destructive animate-in fade-in slide-in-from-top mt-2 text-xs duration-300">Wystąpił błąd podczas przesyłania pliku. Spróbuj ponownie.</p>}
        {fileUploadState.files.length > 0 && (
          <div className="mt-2">
            {fileUploadState.files.map((f) => (
              <div key={f.id} className="text-xs">
                {f.file.name}: {f.status}
                {f.status === 'uploading' && ` (${Math.round(f.progress)}%)`}
                {f.status === 'error' && <span className="text-text-destructive"> - Błąd</span>}
              </div>
            ))}
          </div>
        )}
      </DropzoneDragArea>
    </NodeViewWrapper>
  );
}
